import { API_BASE_URL } from '../config/apiConfig';

const CLIENT_LOG_URL = `${API_BASE_URL}/api/v1/client/error`;

/** Suppress repeats of the SAME message within this window (de-dupes error bursts/loops). */
const DEDUPE_WINDOW_MS = 10_000;
/** Hard cap on distinct messages reported per minute, so a storm of varied errors can't flood. */
const MAX_REPORTS_PER_MINUTE = 10;
const RATE_WINDOW_MS = 60_000;

/** Last time each distinct message was reported, used for per-message de-duping. */
const lastReportByMessage = new Map<string, number>();
/** Timestamps of recent reports, used for the overall per-minute cap. */
const recentReports: number[] = [];

export type ClientLogLevel = 'error' | 'warn';

/**
 * Reports a client-side error to the backend, which forwards it to Telegram.
 * Fire-and-forget. De-duped per message (10s) and capped overall (10/min) so a
 * single recurring error or an error storm can't spam the channel, while distinct
 * errors still get through.
 */
export function logClientError(message: string, context?: string, level: ClientLogLevel = 'error'): void {
  const trimmed = message.slice(0, 500);
  const now = Date.now();

  // Per-message de-dupe: drop an identical message seen within the window.
  const lastForMessage = lastReportByMessage.get(trimmed);
  if (lastForMessage !== undefined && now - lastForMessage < DEDUPE_WINDOW_MS) return;

  // Overall cap: prune old timestamps, then drop if we're over budget for the minute.
  while (recentReports.length > 0 && now - recentReports[0] > RATE_WINDOW_MS) {
    recentReports.shift();
  }
  if (recentReports.length >= MAX_REPORTS_PER_MINUTE) return;

  lastReportByMessage.set(trimmed, now);
  recentReports.push(now);

  // Bound the de-dupe map so long sessions with many distinct errors don't grow it forever.
  if (lastReportByMessage.size > 100) {
    for (const [key, ts] of lastReportByMessage) {
      if (now - ts > DEDUPE_WINDOW_MS) lastReportByMessage.delete(key);
    }
  }

  const payload: { message: string; context?: string; level?: ClientLogLevel } = { message: trimmed, context };
  // Preserve the existing error payload shape; warnings are opt-in so older backends
  // continue treating unknown messages as errors during rolling deploys.
  if (level === 'warn') payload.level = level;

  fetch(CLIENT_LOG_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {/* ignore reporting failures */});
}

/** Report an expected client-environment failure without triggering error alerts. */
export function logClientWarning(message: string, context?: string): void {
  logClientError(message, context, 'warn');
}
